/**
 * Async half of the artwork rendition lookup. `planArtworkSrc` is pure and
 * cannot know whether a convention-named Storage object exists; this hook
 * resolves that with `getDownloadURL`, caching per session so a wall of tiles
 * costs one round trip per distinct path, not one per render.
 *
 * Return values: `undefined` while pending, `null` on a confirmed miss, else
 * the download URL. Callers feed this straight into `resolveArtworkSrc`.
 */
import * as Sentry from "@sentry/react"
import { useEffect, useMemo, useState } from "react"

import Firestore from "#classes/Firestore"
import { type StudentArtwork } from "#customTypes/composedTypes/studentTypes"
import {
  planArtworkSrc,
  type RenditionTier,
  resolveArtworkSrc,
} from "#utils/artworkRenditionHelpers"

type RenditionLookup = string | null

const resolvedCache = new Map<string, RenditionLookup>()
const inflight = new Map<string, Promise<RenditionLookup>>()
const subscribers = new Map<string, Set<() => void>>()

/**
 * Bumped by every invalidation. A probe that started before a write must not
 * cache its answer, or an upload that lands mid-probe leaves the tile pinned to
 * the original for the rest of the session.
 */
const generations = new Map<string, number>()

const getGeneration = (path: string): number => generations.get(path) ?? 0

const lookupRendition = (path: string): Promise<RenditionLookup> => {
  const cached = resolvedCache.get(path)
  if (cached !== undefined) return Promise.resolve(cached)

  const pending = inflight.get(path)
  if (pending) return pending

  const generation = getGeneration(path)
  const cacheIfCurrent = (value: RenditionLookup): RenditionLookup => {
    if (getGeneration(path) === generation) resolvedCache.set(path, value)
    return value
  }

  const request = Firestore.getInstance()
    .getDownloadURLOrNull(path)
    .then(cacheIfCurrent)
    .catch((error: unknown) => {
      // A miss is already null; anything else is unexpected. Surface it, and
      // fall back to the original so the tile still renders.
      Sentry.captureException(error)
      return cacheIfCurrent(null)
    })
    .finally(() => {
      // Only clear our own entry — an invalidation may have already replaced it.
      if (inflight.get(path) === request) inflight.delete(path)
    })

  inflight.set(path, request)
  return request
}

/**
 * Drop a cached answer, e.g. right after uploading the rendition it names, and
 * re-probe anything already on screen. Discards the in-flight probe as well:
 * that request predates the write, so its answer is stale by definition.
 * Mounted hooks keep showing their last known value while they re-probe.
 */
export const invalidateRenditionUrl = (path: string): void => {
  generations.set(path, getGeneration(path) + 1)
  resolvedCache.delete(path)
  inflight.delete(path)
  subscribers.get(path)?.forEach((reprobe) => reprobe())
}

export const useRenditionUrl = (
  path: string | null
): RenditionLookup | undefined => {
  const [state, setState] = useState<{
    path: string | null
    value: RenditionLookup | undefined
  }>(() => ({
    path,
    value: path === null ? null : resolvedCache.get(path),
  }))

  useEffect(() => {
    if (path === null) return

    let cancelled = false

    const probe = () => {
      const cached = resolvedCache.get(path)
      if (cached !== undefined) {
        setState({ path, value: cached })
        return
      }

      // Drop answers from a probe that an invalidation has since superseded,
      // otherwise a pre-write "missing" can land after the post-write hit.
      const generation = getGeneration(path)
      // Only a FIRST probe for this path goes pending. A re-probe (invalidation
      // while mounted) keeps the last known value on screen until the new
      // answer lands — pending resolves to the first fallback, the full-size
      // original, so clearing here would flash it on every rendition write.
      setState((prev) =>
        prev.path === path ? prev : { path, value: undefined }
      )
      void lookupRendition(path).then((url) => {
        if (cancelled || getGeneration(path) !== generation) return
        setState({ path, value: url })
      })
    }

    probe()

    // Re-probe on invalidation so a tile already on screen picks up a rendition
    // that was uploaded after its first probe resolved.
    const pathSubscribers = subscribers.get(path) ?? new Set<() => void>()
    pathSubscribers.add(probe)
    subscribers.set(path, pathSubscribers)

    return () => {
      cancelled = true
      pathSubscribers.delete(probe)
      if (pathSubscribers.size === 0) subscribers.delete(path)
    }
  }, [path])

  if (path === null) return null
  return state.path === path ? state.value : resolvedCache.get(path)
}

/** Plans, probes, and resolves the `src` a tile should render for `tier`. */
export const useArtworkSrc = (
  artwork: StudentArtwork,
  tier: RenditionTier
): string => {
  const appBucket = Firestore.getInstance().getAppBucket()
  const plan = useMemo(
    () => planArtworkSrc(artwork, tier, appBucket),
    [artwork, tier, appBucket]
  )
  const renditionUrl = useRenditionUrl(plan.renditionPath)
  return resolveArtworkSrc(plan, renditionUrl)
}
